/**
 * PIQ 8.3.2: Debug Data Dump Panel
 * =================================
 * Dev/admin only panel to inspect raw data shapes for debugging.
 * Shows: requiredMetrics.rows, feed items, Google detection counts.
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Bug, Code, Database } from 'lucide-react';

interface DebugDataDumpProps {
  requiredMetricsRows?: any[];
  feedItems?: Array<{
    id: string;
    title?: string;
    summary?: string;
    sourceType?: string;
    category?: string;
    group?: string;
  }>;
  brainContract?: any;
  execBriefData?: any;
}

// Only show in dev or with ?admin=true
function shouldShowDebug(): boolean {
  if (typeof window === 'undefined') return false;
  const isDev = process.env.NODE_ENV === 'development';
  const isAdmin = new URLSearchParams(window.location.search).get('admin') === 'true';
  return isDev || isAdmin;
}

export function DebugDataDump({
  requiredMetricsRows = [],
  feedItems = [],
  brainContract,
  execBriefData,
}: DebugDataDumpProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'metrics' | 'feed' | 'raw'>('metrics');

  if (!shouldShowDebug()) return null;

  const googleCount = feedItems.filter(
    item => item.sourceType === 'google' || item.sourceType === 'meta'
  ).length;

  const marketingItems = feedItems.filter(
    item => item.group === 'marketing' || item.sourceType === 'google' || item.sourceType === 'meta'
  );

  const patternItems = feedItems.filter(item => item.group === 'patterns');

  return (
    <div className="mt-4 border border-dashed border-amber-400 rounded-lg bg-amber-50/50">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-amber-700 hover:bg-amber-100/50 transition-colors"
      >
        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <Bug className="w-4 h-4" />
        <span>Debug Data Dump (dev only)</span>
        <span className="ml-auto text-xs text-amber-600">
          {requiredMetricsRows.length} metrics | {feedItems.length} feed | {googleCount} google
        </span>
      </button>

      {isOpen && (
        <div className="px-3 pb-3 border-t border-amber-200">
          {/* Tabs */}
          <div className="flex gap-1 mt-2 mb-3">
            <button
              onClick={() => setActiveTab('metrics')}
              className={`px-2 py-1 text-xs rounded ${
                activeTab === 'metrics'
                  ? 'bg-amber-200 text-amber-900'
                  : 'bg-amber-100 text-amber-700 hover:bg-amber-150'
              }`}
            >
              <Database className="w-3 h-3 inline mr-1" />
              Metrics ({requiredMetricsRows.length})
            </button>
            <button
              onClick={() => setActiveTab('feed')}
              className={`px-2 py-1 text-xs rounded ${
                activeTab === 'feed'
                  ? 'bg-amber-200 text-amber-900'
                  : 'bg-amber-100 text-amber-700 hover:bg-amber-150'
              }`}
            >
              Feed Items ({feedItems.length})
            </button>
            <button
              onClick={() => setActiveTab('raw')}
              className={`px-2 py-1 text-xs rounded ${
                activeTab === 'raw'
                  ? 'bg-amber-200 text-amber-900'
                  : 'bg-amber-100 text-amber-700 hover:bg-amber-150'
              }`}
            >
              <Code className="w-3 h-3 inline mr-1" />
              Raw JSON
            </button>
          </div>

          {/* Metrics Tab */}
          {activeTab === 'metrics' && (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-amber-800 uppercase">
                requiredMetrics.rows (first 5)
              </h4>
              {requiredMetricsRows.length === 0 ? (
                <p className="text-xs text-amber-600 italic">No rows available</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border border-amber-200">
                    <thead className="bg-amber-100">
                      <tr>
                        <th className="px-2 py-1 text-left text-amber-800">Channel</th>
                        <th className="px-2 py-1 text-left text-amber-800">Revenue Shape</th>
                        <th className="px-2 py-1 text-left text-amber-800">WoW Shape</th>
                        <th className="px-2 py-1 text-left text-amber-800">YoY Shape</th>
                        <th className="px-2 py-1 text-left text-amber-800">Raw Keys</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requiredMetricsRows.slice(0, 5).map((row, idx) => (
                        <tr key={idx} className="border-t border-amber-100">
                          <td className="px-2 py-1 font-medium">
                            {row.channel || row.channelName || row.name || '?'}
                          </td>
                          <td className="px-2 py-1 font-mono text-[10px]">
                            {describeShape(row.revenue, row.revenueValue, row.revenueFormatted)}
                          </td>
                          <td className="px-2 py-1 font-mono text-[10px]">
                            {describeShape(row.wow, row.wowValue, row.wowFormatted, row.wowPct)}
                          </td>
                          <td className="px-2 py-1 font-mono text-[10px]">
                            {describeShape(row.yoy, row.yoyValue, row.yoyFormatted, row.yoyPct)}
                          </td>
                          <td className="px-2 py-1 font-mono text-[10px]">
                            {Object.keys(row).join(', ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Feed Tab */}
          {activeTab === 'feed' && (
            <div className="space-y-3">
              {/* Google/Marketing Summary */}
              <div className="p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                <span className="font-semibold text-blue-800">Marketing Detection:</span>
                <span className="ml-2 text-blue-700">
                  {googleCount} google/meta items | {marketingItems.length} in marketing group
                </span>
                {patternItems.some(p => p.sourceType === 'google' || p.sourceType === 'meta') && (
                  <span className="ml-2 text-red-600 font-bold">
                    ⚠️ VIOLATION: Google/Meta in patterns!
                  </span>
                )}
              </div>

              <h4 className="text-xs font-semibold text-amber-800 uppercase">
                Feed Items (first 10)
              </h4>
              <div className="overflow-x-auto max-h-60 overflow-y-auto">
                <table className="w-full text-xs border border-amber-200">
                  <thead className="bg-amber-100 sticky top-0">
                    <tr>
                      <th className="px-2 py-1 text-left text-amber-800">Title</th>
                      <th className="px-2 py-1 text-left text-amber-800">SourceType</th>
                      <th className="px-2 py-1 text-left text-amber-800">Category</th>
                      <th className="px-2 py-1 text-left text-amber-800">Group</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feedItems.slice(0, 10).map((item, idx) => (
                      <tr
                        key={item.id || idx}
                        className={`border-t border-amber-100 ${
                          item.sourceType === 'google' || item.sourceType === 'meta'
                            ? 'bg-blue-50'
                            : ''
                        }`}
                      >
                        <td className="px-2 py-1 max-w-[200px] truncate">
                          {item.title || item.summary || '—'}
                        </td>
                        <td className="px-2 py-1 font-mono">
                          <span
                            className={`px-1 rounded ${
                              item.sourceType === 'google'
                                ? 'bg-blue-200 text-blue-800'
                                : item.sourceType === 'meta'
                                ? 'bg-sky-200 text-sky-800'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {item.sourceType || '?'}
                          </span>
                        </td>
                        <td className="px-2 py-1">{item.category || '—'}</td>
                        <td className="px-2 py-1">{item.group || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Raw JSON Tab */}
          {activeTab === 'raw' && (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-amber-800 uppercase">
                Raw ExecBrief Data (truncated)
              </h4>
              <pre className="text-[10px] font-mono bg-gray-900 text-green-400 p-2 rounded overflow-x-auto max-h-40 overflow-y-auto">
                {JSON.stringify(
                  {
                    requiredMetrics: {
                      rows: requiredMetricsRows.slice(0, 3),
                    },
                    whatMattersNow: execBriefData?.whatMattersNow?.slice(0, 2),
                    doTheseNow: execBriefData?.doTheseNow?.slice(0, 2),
                  },
                  null,
                  2
                )}
              </pre>
              
              <h4 className="text-xs font-semibold text-amber-800 uppercase mt-3">
                googleAdsAdvisory from BrainContract
              </h4>
              <pre className="text-[10px] font-mono bg-gray-900 text-green-400 p-2 rounded overflow-x-auto max-h-40 overflow-y-auto">
                {JSON.stringify(brainContract?.googleAdsAdvisory?.slice(0, 3) || [], null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Helper to describe data shape for debugging
function describeShape(...values: any[]): string {
  for (const val of values) {
    if (val === null || val === undefined) continue;
    if (typeof val === 'object' && val !== null) {
      const keys = Object.keys(val).slice(0, 3);
      return `{${keys.join(',')}}`;
    }
    if (typeof val === 'number') return `num:${val}`;
    if (typeof val === 'string') return `str:"${val.slice(0, 15)}"`;
  }
  return '—';
}

export default DebugDataDump;
